/**
 * Interfeys matnlari (chrome) — uz / ru / en.
 *
 * Kontent matnlari (yangiliklar, sahifalar, xizmat tavsiflari, FAQ) bu yerda
 * saqlanmaydi: ular CMS'dan `Accept-Language` bo'yicha to'rt tilda keladi
 * (`apps.portal`, `MultilingualSerializerField`). Backend ulanmagan konturda
 * `shared/data/content.ts` dagi zaxira kontent ko'rsatiladi.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { loginEn, loginRu, loginUz } from "./loginMethods";
import { publicEn, publicRu, publicUz } from "./publicHome";
import { REGIONAL_EN, REGIONAL_RU } from "./regional";

export const LANGS = [
  { code: "uz", label: "O'zbekcha", short: "UZ" },
  { code: "ru", label: "Русский", short: "RU" },
  { code: "en", label: "English", short: "EN" },
] as const;

export type Lang = (typeof LANGS)[number]["code"];

type Dict = Record<string, string>;

const uz: Dict = {
  "app.name": "Smart Forestry",
  "app.tagline": "O'rmonlar milliy yagona platformasi",
  "app.agency": "O'rmonlar milliy yagona platformasi",
  "app.gov": "Rasmiy davlat axborot tizimi",

  "nav.subsystems": "Kichik tizimlar",
  "nav.services": "Elektron xizmatlar",
  "nav.map": "Ochiq xarita",
  "nav.news": "Yangiliklar",
  "nav.contacts": "Aloqa",
  "nav.faq": "Savol-javob",
  "nav.openData": "Ochiq ma'lumotlar",
  "nav.about": "Biz haqimizda",
  "nav.appeal": "Murojaat",
  "nav.tracking": "Kuzatish",
  "nav.guides": "Yo'riqnomalar",
  "nav.media": "Media",
  "nav.app": "Mobil ilova",
  "nav.more": "Yana",

  "action.login": "Kirish",
  "action.logout": "Chiqish",
  "action.menu": "Menyu",
  "action.close": "Yopish",
  "action.more": "Batafsil",
  "stat.shareOfCountry": "respublika hududining\u00a0{v}",
  "stats.title": "Ochiq statistika",
  "stats.byRegion": "Viloyatlar kesimida",
  "stats.byMonth": "Oylar bo'yicha, {year}",
  "stats.other": "Boshqalar",
  "stats.closed": "{n} hal etildi",
  "stats.open": "{n} jarayonda",
  "stats.noData": "Ma'lumot hali yo'q",
  "action.all": "Barchasi",
  "action.search": "Qidirish",
  "action.check": "Tekshirish",
  "action.refresh": "Yangilash",
  "action.export": "Eksport",
  "action.back": "Ortga",
  "action.retry": "Qayta urinish",
  "action.openMap": "Xaritani ochish",
  "action.contact": "Bog'lanish",
  "action.send": "Yuborish",

  "ai.open": "AI yordamchini ochish",
  "ai.close": "Yordamchini yopish",
  "ai.typing": "Javob yozilmoqda",

  "pref.language": "Til",
  "pref.theme": "Mavzu",
  "pref.theme.light": "Yorug'",
  "pref.theme.dark": "Qorong'i",
  "pref.theme.hc": "Yuqori kontrast",
  "pref.textSize": "Matn o'lchami",
  "pref.textSize.large": "Kattalashtirilgan matn",
  "a11y.skip": "Asosiy kontentga o'tish",
  "a11y.visionMode": "Ko'rish imkoniyati cheklanganlar uchun",

  "hotline.label": "Ishonch telefoni",
  "hotline.hours": "24/7, bepul",

  "state.loading": "Yuklanmoqda…",
  "state.demo": "Demo ma'lumot",
  "state.demoNote":
    "Backend ulanmagan — ko'rsatkichlar namunaviy. Ulangach ma'lumot avtomatik almashadi.",
  "state.error": "Ma'lumotni yuklab bo'lmadi",
  "state.empty": "Ma'lumot yo'q",
  "state.emptyHint": "Tanlangan davr uchun yozuv topilmadi.",
  "state.noAccess": "Bu ma'lumotni ko'rish uchun huquqingiz yetarli emas.",

  "auth.title": "Tizimga kirish",
  "auth.tab.email": "E-pochta orqali",
  "auth.tab.oneid": "OneID orqali",
  "auth.email": "E-pochta",
  "auth.password": "Parol",
  "auth.forgot": "Parolni unutdingizmi?",
  "auth.trustDevice": "Bu qurilmaga 30 kun ishonish",
  "auth.showPassword": "Parolni ko'rsatish",
  "auth.hidePassword": "Parolni yashirish",
  "auth.submit": "Kirish",
  "auth.submitting": "Tekshirilmoqda…",
  "auth.oneid.button": "OneID bilan davom etish",
  "auth.oneid.title": "Yagona identifikatsiya tizimi",
  "auth.oneid.sub": "id.egov.uz — davlat xizmatlari hisobi",
  "auth.mfa.title": "Tasdiqlash kodi",
  "auth.mfa.sub": "Ilova yoki SMS orqali kelgan 6 xonali kodni kiriting",
  "auth.mfa.label": "Bir martalik kod",
  "auth.mfa.resend": "Kodni qayta yuborish",
  "auth.mfa.back": "Boshqa hisob bilan kirish",
  "auth.backToPortal": "Bosh sahifaga qaytish",
  "auth.err.required": "Barcha majburiy maydonlarni to'ldiring.",
  "auth.err.email": "E-pochta manzili noto'g'ri formatda.",
  "auth.err.otp": "Kod 6 ta raqamdan iborat bo'lishi kerak.",
  "auth.err.network": "Server bilan aloqa yo'q. Keyinroq urinib ko'ring.",
  "auth.foot": "Barcha huquqlar himoyalangan. Smart Forestry Developers Group",

  "dash.breadcrumb": "Kabinet",
  "dash.title": "Boshqaruv paneli",
  "dash.sub": "Operativ holat va asosiy ko'rsatkichlar",
  "dash.period.day": "Kun",
  "dash.period.week": "Hafta",
  "dash.period.month": "Oy",
  "dash.period.quarter": "Chorak",
  "dash.period.year": "Yil",
  "dash.searchPh": "Hodisa, murojaat, uchastka yoki hujjat…",
  "dash.notifications": "Bildirishnomalar",
  "dash.kpi.events": "Qayd etilgan hodisalar",
  "dash.kpi.appeals": "Fuqaro murojaatlari",
  "dash.kpi.confirm": "O'rtacha tasdiqlash vaqti",
  "dash.kpi.resolve": "O'rtacha hal qilish vaqti",
  "dash.kpi.sla": "SLA bajarilishi",
  "dash.kpi.falseAlarm": "Yolg'on signal ulushi",
  "dash.chart.title": "Hodisalar va murojaatlar dinamikasi",
  "dash.chart.sub": "Kunlik qiymatlar, tanlangan davr",
  "dash.chart.events": "Hodisalar",
  "dash.chart.appeals": "Murojaatlar",
  "dash.sla.title": "Murojaatlar holati",
  "dash.sla.sub": "Muddat bo'yicha taqsimot",
  "dash.regions.title": "Hududlar kesimida",
  "dash.regions.sub": "Davr ichidagi ochiq hodisalar",
  "dash.regions.legendLow": "kam",
  "dash.regions.legendHigh": "ko'p",
  "dash.table.title": "So'nggi murojaatlar",
  "dash.table.id": "Raqam",
  "dash.table.subject": "Mavzu",
  "dash.table.region": "Hudud",
  "dash.table.operator": "Operator",
  "dash.table.deadline": "Muddat",
  "dash.table.status": "Holat",
  "dash.feed.title": "Faollik lentasi",
  "dash.feed.sub": "Real vaqt rejimida",
  "dash.workload.title": "Operatorlar yuklamasi",
  "dash.workload.sub": "Davr ichida biriktirilgan murojaatlar",
  "dash.integrations.title": "Integratsiyalar holati",
  "dash.integrations.sub": "Tashqi tizimlar bilan almashinuv",
  "dash.status.new": "Yangi",
  "dash.status.inWork": "Ishda",
  "dash.status.done": "Bajarildi",
  "dash.status.overdue": "Muddati o'tgan",
  "dash.unit.min": "daq",
  "dash.unit.hour": "soat",
  "dash.unit.pcs": "ta",
  "dash.vsPrev": "oldingi davrga nisbatan",
  "dash.periodLabel": "Hisobot davri",
  "dash.scope.national": "Respublika qamrovi",
  "dash.scope.region": "Hududiy qamrov",
  "dash.system": "Tizim",
  "dash.sandbox": "SANDBOX",
  "dash.live.title": "Ayni damdagi holat",
  "dash.live.sub": "Yopilmagan hodisalar, murojaatlar va kuzatuv qurilmalari",
  "dash.live.events": "Faol hodisalar",
  "dash.live.pending": "Tasdiq kutmoqda",
  "dash.live.appeals": "Ochiq murojaatlar",
  "dash.live.overdue": "Muddati o'tgan",
  "dash.live.cameras": "Kameralar onlayn",
  "dash.section.period": "Davr ko'rsatkichlari",
  "dash.section.periodSub": "Tanlangan davr bo'yicha hisoblangan qiymatlar",
  "dash.section.flow": "Dinamika va muddat",
  "dash.section.flowSub": "Oqim qanday o'zgargani va SLA holati",
  "dash.section.geo": "Hududlar va faollik",
  "dash.section.geoSub": "Murojaatlarning hududiy taqsimoti va tizimdagi so'nggi harakatlar",
  "dash.section.work": "Murojaatlar va tizim holati",
  "dash.section.workSub": "So'nggi murojaatlar, operatorlar yuklamasi va integratsiyalar",
  "dash.kpi.ai": "AI aniqlagan hodisalar",
  "dash.kpi.eventsHint": "Davr ichida qayd etilgan",
  "dash.kpi.appealsHint": "Fuqarolardan kelgan",
  "dash.kpi.confirmHint": "Aniqlashdan tasdiqlashgacha",
  "dash.kpi.resolveHint": "Aniqlashdan yopilishgacha",
  "dash.kpi.slaHint": "Muddat ichida ko'rilganlar ulushi",
  "dash.kpi.aiHint": "Kamera oqimidan avtomatik",
  "dash.sla.green": "Muddat ichida",
  "dash.sla.yellow": "Muddat yaqin",
  "dash.sla.red": "Muddat o'tgan",
  "dash.sla.black": "Ikki baravar kechikkan",
  "dash.appealStatus.new": "Yangi",
  "dash.appealStatus.in_review": "Ko'rib chiqilmoqda",
  "dash.appealStatus.routed": "Yo'naltirildi",
  "dash.appealStatus.in_progress": "Ijroda",
  "dash.appealStatus.result_entered": "Natija kiritildi",
  "dash.appealStatus.closed": "Yakunlandi",

  /* --- kabinet modullari (uz; boshqa tillarda shu matn zaxira sifatida ishlatiladi) --- */
  "cab.searchPh": "Bo'lim nomini yozing…",
  "cab.filters": "Filtrlar",
  "cab.total": "Jami",
  "page.next": "Keyingi",
  "action.reset": "Tozalash",
  "action.open": "Ochish",
  "state.noRows": "Tanlangan filtrga mos yozuv topilmadi.",
};

const ru: Dict = {
  "app.name": "Smart Forestry",
  "app.tagline": "Единая информационная платформа лесного хозяйства",
  "app.agency": "Агентство лесного хозяйства Республики Узбекистан",
  "app.gov": "Официальная государственная информационная система",

  "nav.subsystems": "Подсистемы",
  "nav.services": "Электронные услуги",
  "nav.map": "Открытая карта",
  "nav.news": "Новости",
  "nav.contacts": "Контакты",
  "nav.faq": "Вопросы и ответы",
  "nav.openData": "Открытые данные",
  "nav.about": "О нас",
  "nav.appeal": "Обращение",
  "nav.tracking": "Отследить",
  "nav.guides": "Руководства",
  "nav.media": "Медиа",
  "nav.app": "Мобильное приложение",
  "nav.more": "Ещё",

  "action.login": "Вход",
  "action.logout": "Выход",
  "action.menu": "Меню",
  "action.close": "Закрыть",
  "action.more": "Подробнее",
  "stat.shareOfCountry": "{v}\u00a0территории республики",
  "stats.title": "Открытая статистика",
  "stats.byRegion": "По регионам",
  "stats.byMonth": "По месяцам, {year}",
  "stats.other": "Другие",
  "stats.closed": "{n} решено",
  "stats.open": "{n} в работе",
  "stats.noData": "Данных пока нет",
  "action.all": "Все",
  "action.search": "Поиск",
  "action.check": "Проверить",
  "action.refresh": "Обновить",
  "action.export": "Экспорт",
  "action.back": "Назад",
  "action.retry": "Повторить",
  "action.openMap": "Открыть карту",
  "action.contact": "Связаться",
  "action.send": "Отправить",

  "ai.open": "Открыть ИИ-помощника",
  "ai.close": "Закрыть помощника",
  "ai.typing": "Печатает ответ",

  "pref.language": "Язык",
  "pref.theme": "Тема",
  "pref.theme.light": "Светлая",
  "pref.theme.dark": "Тёмная",
  "pref.theme.hc": "Высокий контраст",
  "pref.textSize": "Размер текста",
  "pref.textSize.large": "Увеличенный текст",
  "a11y.skip": "Перейти к основному содержанию",
  "a11y.visionMode": "Для слабовидящих",

  "hotline.label": "Телефон доверия",
  "hotline.hours": "24/7, бесплатно",

  "state.loading": "Загрузка…",
  "state.demo": "Демонстрационные данные",
  "state.demoNote":
    "Бэкенд не подключён — показатели демонстрационные. После подключения данные обновятся автоматически.",
  "state.error": "Не удалось загрузить данные",
  "state.empty": "Нет данных",
  "state.emptyHint": "За выбранный период записей не найдено.",
  "state.noAccess": "Недостаточно прав для просмотра этих данных.",

  "auth.title": "Вход в систему",
  "auth.tab.email": "По электронной почте",
  "auth.tab.oneid": "Через OneID",
  "auth.email": "Электронная почта",
  "auth.password": "Пароль",
  "auth.forgot": "Забыли пароль?",
  "auth.trustDevice": "Доверять этому устройству 30 дней",
  "auth.showPassword": "Показать пароль",
  "auth.hidePassword": "Скрыть пароль",
  "auth.submit": "Войти",
  "auth.submitting": "Проверка…",
  "auth.oneid.button": "Продолжить через OneID",
  "auth.oneid.title": "Единая система идентификации",
  "auth.oneid.sub": "id.egov.uz — учётная запись госуслуг",
  "auth.mfa.title": "Код подтверждения",
  "auth.mfa.sub": "Введите 6-значный код из приложения или SMS",
  "auth.mfa.label": "Одноразовый код",
  "auth.mfa.resend": "Отправить код повторно",
  "auth.mfa.back": "Войти под другой учётной записью",
  "auth.backToPortal": "На главную страницу",
  "auth.err.required": "Заполните все обязательные поля.",
  "auth.err.email": "Некорректный формат электронной почты.",
  "auth.err.otp": "Код должен состоять из 6 цифр.",
  "auth.err.network": "Нет связи с сервером. Повторите попытку позже.",
  "auth.foot": "Все права защищены. Smart Forestry Developers Group",

  "dash.breadcrumb": "Кабинет",
  "dash.title": "Панель управления",
  "dash.sub": "Оперативная обстановка и ключевые показатели",
  "dash.period.day": "День",
  "dash.period.week": "Неделя",
  "dash.period.month": "Месяц",
  "dash.period.quarter": "Квартал",
  "dash.period.year": "Год",
  "dash.searchPh": "Событие, обращение, участок или документ…",
  "dash.notifications": "Уведомления",
  "dash.kpi.events": "Зарегистрированные события",
  "dash.kpi.appeals": "Обращения граждан",
  "dash.kpi.confirm": "Среднее время подтверждения",
  "dash.kpi.resolve": "Среднее время решения",
  "dash.kpi.sla": "Соблюдение SLA",
  "dash.kpi.falseAlarm": "Доля ложных сигналов",
  "dash.chart.title": "Динамика событий и обращений",
  "dash.chart.sub": "Значения по дням за выбранный период",
  "dash.chart.events": "События",
  "dash.chart.appeals": "Обращения",
  "dash.sla.title": "Статусы обращений",
  "dash.sla.sub": "Распределение по срокам",
  "dash.regions.title": "В разрезе регионов",
  "dash.regions.sub": "Открытые события за период",
  "dash.regions.legendLow": "мало",
  "dash.regions.legendHigh": "много",
  "dash.table.title": "Последние обращения",
  "dash.table.id": "Номер",
  "dash.table.subject": "Тема",
  "dash.table.region": "Регион",
  "dash.table.operator": "Оператор",
  "dash.table.deadline": "Срок",
  "dash.table.status": "Статус",
  "dash.feed.title": "Лента активности",
  "dash.feed.sub": "В реальном времени",
  "dash.workload.title": "Нагрузка операторов",
  "dash.workload.sub": "Закреплённые обращения за период",
  "dash.integrations.title": "Состояние интеграций",
  "dash.integrations.sub": "Обмен с внешними системами",
  "dash.status.new": "Новое",
  "dash.status.inWork": "В работе",
  "dash.status.done": "Выполнено",
  "dash.status.overdue": "Просрочено",
  "dash.unit.min": "мин",
  "dash.unit.hour": "ч",
  "dash.unit.pcs": "шт",
  "dash.vsPrev": "к предыдущему периоду",
  "dash.periodLabel": "Отчётный период",
  "dash.scope.national": "Республиканский охват",
  "dash.scope.region": "Региональный охват",
  "dash.system": "Система",
  "dash.sandbox": "SANDBOX",
  "dash.live.title": "Текущее состояние",
  "dash.live.sub": "Незакрытые события, обращения и устройства наблюдения",
  "dash.live.events": "Активные события",
  "dash.live.pending": "Ожидают подтверждения",
  "dash.live.appeals": "Открытые обращения",
  "dash.live.overdue": "Просроченные",
  "dash.live.cameras": "Камеры онлайн",
  "dash.section.period": "Показатели за период",
  "dash.section.periodSub": "Значения, рассчитанные за выбранный период",
  "dash.section.flow": "Динамика и сроки",
  "dash.section.flowSub": "Как менялся поток и состояние SLA",
  "dash.section.geo": "Регионы и активность",
  "dash.section.geoSub": "Распределение обращений по регионам и последние действия",
  "dash.section.work": "Обращения и состояние систем",
  "dash.section.workSub": "Последние обращения, нагрузка операторов и интеграции",
  "dash.kpi.ai": "События, выявленные ИИ",
  "dash.kpi.eventsHint": "Зарегистрировано за период",
  "dash.kpi.appealsHint": "Поступило от граждан",
  "dash.kpi.confirmHint": "От обнаружения до подтверждения",
  "dash.kpi.resolveHint": "От обнаружения до закрытия",
  "dash.kpi.slaHint": "Доля рассмотренных в срок",
  "dash.kpi.aiHint": "Автоматически с камер",
  "dash.sla.green": "В срок",
  "dash.sla.yellow": "Срок близок",
  "dash.sla.red": "Срок истёк",
  "dash.sla.black": "Просрочено вдвое",
  "dash.appealStatus.new": "Новое",
  "dash.appealStatus.in_review": "На рассмотрении",
  "dash.appealStatus.routed": "Направлено",
  "dash.appealStatus.in_progress": "В работе",
  "dash.appealStatus.result_entered": "Результат внесён",
  "dash.appealStatus.closed": "Завершено",
};

const en: Dict = {
  "app.name": "Smart Forestry",
  "app.tagline": "Unified information platform for forestry",
  "app.agency": "Forestry Agency of the Republic of Uzbekistan",
  "app.gov": "Official state information system",

  "nav.subsystems": "Subsystems",
  "nav.services": "E-services",
  "nav.map": "Open map",
  "nav.news": "News",
  "nav.contacts": "Contacts",
  "nav.faq": "FAQ",
  "nav.openData": "Open data",
  "nav.about": "About",
  "nav.appeal": "Report",
  "nav.tracking": "Track",
  "nav.guides": "Guides",
  "nav.media": "Media",
  "nav.app": "Mobile app",
  "nav.more": "More",

  "action.login": "Sign in",
  "action.logout": "Sign out",
  "action.menu": "Menu",
  "action.close": "Close",
  "action.more": "Learn more",
  "stat.shareOfCountry": "{v}\u00a0of the national territory",
  "stats.title": "Open statistics",
  "stats.byRegion": "By region",
  "stats.byMonth": "By month, {year}",
  "stats.other": "Other",
  "stats.closed": "{n} resolved",
  "stats.open": "{n} in progress",
  "stats.noData": "No data yet",
  "action.all": "View all",
  "action.search": "Search",
  "action.check": "Check",
  "action.refresh": "Refresh",
  "action.export": "Export",
  "action.back": "Back",
  "action.retry": "Try again",
  "action.openMap": "Open map",
  "action.contact": "Contact us",
  "action.send": "Send",

  "ai.open": "Open the AI assistant",
  "ai.close": "Close the assistant",
  "ai.typing": "Typing a reply",

  "pref.language": "Language",
  "pref.theme": "Theme",
  "pref.theme.light": "Light",
  "pref.theme.dark": "Dark",
  "pref.theme.hc": "High contrast",
  "pref.textSize": "Text size",
  "pref.textSize.large": "Larger text",
  "a11y.skip": "Skip to main content",
  "a11y.visionMode": "Accessibility mode",

  "hotline.label": "Hotline",
  "hotline.hours": "24/7, toll-free",

  "state.loading": "Loading…",
  "state.demo": "Demo data",
  "state.demoNote":
    "Backend is not connected — figures are illustrative and will refresh automatically once it is.",
  "state.error": "Could not load data",
  "state.empty": "No data",
  "state.emptyHint": "No records for the selected period.",
  "state.noAccess": "You do not have permission to view this data.",

  "auth.title": "Sign in",
  "auth.tab.email": "With e-mail",
  "auth.tab.oneid": "With OneID",
  "auth.email": "E-mail",
  "auth.password": "Password",
  "auth.forgot": "Forgot password?",
  "auth.trustDevice": "Trust this device for 30 days",
  "auth.showPassword": "Show password",
  "auth.hidePassword": "Hide password",
  "auth.submit": "Sign in",
  "auth.submitting": "Verifying…",
  "auth.oneid.button": "Continue with OneID",
  "auth.oneid.title": "National identification system",
  "auth.oneid.sub": "id.egov.uz — public services account",
  "auth.mfa.title": "Verification code",
  "auth.mfa.sub": "Enter the 6-digit code from the app or SMS",
  "auth.mfa.label": "One-time code",
  "auth.mfa.resend": "Send the code again",
  "auth.mfa.back": "Use another account",
  "auth.backToPortal": "Back to home page",
  "auth.err.required": "Please fill in all required fields.",
  "auth.err.email": "The e-mail address format is invalid.",
  "auth.err.otp": "The code must contain 6 digits.",
  "auth.err.network": "No connection to the server. Please try again later.",
  "auth.foot": "All rights reserved. Smart Forestry Developers Group",

  "dash.breadcrumb": "Workspace",
  "dash.title": "Dashboard",
  "dash.sub": "Operational picture and key indicators",
  "dash.period.day": "Day",
  "dash.period.week": "Week",
  "dash.period.month": "Month",
  "dash.period.quarter": "Quarter",
  "dash.period.year": "Year",
  "dash.searchPh": "Event, appeal, plot or document…",
  "dash.notifications": "Notifications",
  "dash.kpi.events": "Registered events",
  "dash.kpi.appeals": "Citizen appeals",
  "dash.kpi.confirm": "Average confirmation time",
  "dash.kpi.resolve": "Average resolution time",
  "dash.kpi.sla": "SLA compliance",
  "dash.kpi.falseAlarm": "False alarm share",
  "dash.chart.title": "Events and appeals over time",
  "dash.chart.sub": "Daily values for the selected period",
  "dash.chart.events": "Events",
  "dash.chart.appeals": "Appeals",
  "dash.sla.title": "Appeal statuses",
  "dash.sla.sub": "Distribution by deadline",
  "dash.regions.title": "By region",
  "dash.regions.sub": "Open events in the period",
  "dash.regions.legendLow": "low",
  "dash.regions.legendHigh": "high",
  "dash.table.title": "Latest appeals",
  "dash.table.id": "Number",
  "dash.table.subject": "Subject",
  "dash.table.region": "Region",
  "dash.table.operator": "Operator",
  "dash.table.deadline": "Deadline",
  "dash.table.status": "Status",
  "dash.feed.title": "Activity feed",
  "dash.feed.sub": "Real time",
  "dash.workload.title": "Operator workload",
  "dash.workload.sub": "Appeals assigned in the period",
  "dash.integrations.title": "Integration health",
  "dash.integrations.sub": "Exchange with external systems",
  "dash.status.new": "New",
  "dash.status.inWork": "In progress",
  "dash.status.done": "Resolved",
  "dash.status.overdue": "Overdue",
  "dash.unit.min": "min",
  "dash.unit.hour": "h",
  "dash.unit.pcs": "pcs",
  "dash.vsPrev": "vs previous period",
  "dash.periodLabel": "Reporting period",
  "dash.scope.national": "National scope",
  "dash.scope.region": "Regional scope",
  "dash.system": "System",
  "dash.sandbox": "SANDBOX",
  "dash.live.title": "Current status",
  "dash.live.sub": "Open events, appeals and monitoring devices",
  "dash.live.events": "Active events",
  "dash.live.pending": "Awaiting confirmation",
  "dash.live.appeals": "Open appeals",
  "dash.live.overdue": "Overdue",
  "dash.live.cameras": "Cameras online",
  "dash.section.period": "Period metrics",
  "dash.section.periodSub": "Values calculated for the selected period",
  "dash.section.flow": "Dynamics and deadlines",
  "dash.section.flowSub": "How the flow changed and the SLA status",
  "dash.section.geo": "Regions and activity",
  "dash.section.geoSub": "Regional split of appeals and the latest system actions",
  "dash.section.work": "Appeals and system status",
  "dash.section.workSub": "Latest appeals, operator workload and integrations",
  "dash.kpi.ai": "AI-detected events",
  "dash.kpi.eventsHint": "Registered in the period",
  "dash.kpi.appealsHint": "Received from citizens",
  "dash.kpi.confirmHint": "From detection to confirmation",
  "dash.kpi.resolveHint": "From detection to closure",
  "dash.kpi.slaHint": "Share handled within the deadline",
  "dash.kpi.aiHint": "Automatically from camera streams",
  "dash.sla.green": "Within deadline",
  "dash.sla.yellow": "Deadline near",
  "dash.sla.red": "Overdue",
  "dash.sla.black": "Twice overdue",
  "dash.appealStatus.new": "New",
  "dash.appealStatus.in_review": "In review",
  "dash.appealStatus.routed": "Routed",
  "dash.appealStatus.in_progress": "In progress",
  "dash.appealStatus.result_entered": "Result entered",
  "dash.appealStatus.closed": "Closed",
};

const DICTS: Record<Lang, Dict> = {
  uz: { ...uz, ...publicUz, ...loginUz },
  ru: { ...ru, ...publicRu, ...loginRu },
  en: { ...en, ...publicEn, ...loginEn },
};

const LOCALES: Record<Lang, string> = { uz: "uz-UZ", ru: "ru-RU", en: "en-GB" };

interface I18nValue {
  lang: Lang;
  locale: string;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function readLang(): Lang {
  try {
    const stored = localStorage.getItem("sf.lang");
    if (stored === "ru" || stored === "en" || stored === "uz") return stored;
  } catch {
    /* localStorage mavjud emas */
  }
  return "uz";
}

/** Joriy til: `tr()` komponentdan tashqarida (modul darajasidagi yordamchilarda) ham ishlashi uchun. */
let activeLang: Lang = "uz";

/**
 * Hududiy kabinet matnlari: kalit — o'zbekcha matnning o'zi, `{0}`, `{1}` — qiymatlar o'rni.
 * Tarjima topilmasa o'zbekcha matn ko'rsatiladi.
 */
export function tr(text: string, args?: ReadonlyArray<string | number>): string {
  const dict = activeLang === "ru" ? REGIONAL_RU : activeLang === "en" ? REGIONAL_EN : null;
  const base = dict?.[text] ?? text;
  return args && args.length ? base.replace(/\{(\d+)\}/g, (_m, n) => String(args[Number(n)] ?? "")) : base;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readLang);
  activeLang = lang;

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem("sf.lang", lang);
    } catch {
      /* saqlab bo'lmadi — sessiya davomida ishlaydi */
    }
  }, [lang]);

  const t = useCallback(
    (key: string) => DICTS[lang][key] ?? DICTS.uz[key] ?? key,
    [lang],
  );

  const value = useMemo<I18nValue>(
    () => ({ lang, locale: LOCALES[lang], setLang: setLangState, t }),
    [lang, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n faqat <I18nProvider> ichida ishlaydi");
  return ctx;
}
